import React, { ReactNode, useLayoutEffect, useRef } from 'react';

/**
 * Scales its text so one line spans the full width of its parent,
 * however wide the screen and whichever font has loaded.
 */
export function FitText({
  children,
  className = '',
  as: Tag = 'div',
}: {
  children: ReactNode;
  className?: string;
  as?: 'div' | 'h1' | 'p';
}) {
  const outer = useRef<HTMLElement>(null);
  const inner = useRef<HTMLSpanElement>(null);

  useLayoutEffect(() => {
    const box = outer.current;
    const text = inner.current;
    if (!box || !text) return;

    let lastWidth = -1;
    let frame = 0;
    const fit = () => {
      const style = getComputedStyle(box);
      const width =
        box.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
      if (width === lastWidth) return;
      lastWidth = width;
      text.style.fontSize = '100px';
      // a hair of slack for the trailing glyph overhang from negative tracking
      const ratio = (width / text.scrollWidth) * 0.985;
      text.style.fontSize = `${Math.floor(100 * ratio * 100) / 100}px`;
    };
    // Defer to the next frame so resizing never loops inside ResizeObserver.
    const schedule = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(fit);
    };

    fit();
    document.fonts?.ready
      .then(() => {
        lastWidth = -1;
        fit();
      })
      .catch(() => {});
    const observer = new ResizeObserver(schedule);
    observer.observe(box);
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, []);

  return (
    <Tag ref={outer as never} className={className}>
      <span ref={inner} style={{ display: 'inline-block', whiteSpace: 'nowrap' }}>
        {children}
      </span>
    </Tag>
  );
}
